<template>
  <div class="enhancify grid-module" ref="module">
    <div class="grid-module__filter">
      <div class="modern-form-group sm-modern-form-group">
        <div v-if="authAuthenticator(permissions.manage.emails.template.modify)"
             class="floating-button">
          <b-button
              class="is-success sm-show"
              @click="onAddHandler"
              position="is-right"
              style="min-width: 160px; margin: auto">
            {{'add event' | upper}}
          </b-button>
        </div>
      </div>
      <div class="modern-form-group">
        <div class="modern-input-labeled">
          <div class="modern-input-controlled">
            <ModernInput type="search"
                         @enter="doSearch"
                         placeholder="Search by event"
                         v-model="filters.searchText"/>
            <div class="modern-input-control">
              <button class="modern-button"
                      @click="doSearch">
                <i class="mdi mdi-magnify"></i>
              </button>
            </div>
          </div>
        </div>
        <div v-if="authAuthenticator(permissions.manage.emails.template.modify)"
             class="floating-button">
          <b-button
              class="is-success"
              @click="onAddHandler"
              position="is-right"
              style="min-width: 160px; margin: auto">
            {{'add event' | upper}}
          </b-button>
        </div>
      </div>
    </div>
    <section class="data-grid">
      <template v-if="!data.length">
        <b-table
            class="modern-table"
            height="100%"
            :columns="columns"
            :loading="loading">
          <template #header="{ column }">
            <div :class="[{'top-sticked': isTopSticky}, column['thclass'],
         'modern-table-header-label']">
              {{ column['label'] }}
            </div>
          </template>
          <template #empty v-if="!loading">
            <div style="padding-top: 1em; overflow: hidden; text-align: center">
          <span class="icon">
            <i class="fal fa-frown fa-3x"></i>
          </span>
              <p>Nothing here</p>
            </div>
          </template>
        </b-table>
      </template>
      <template v-else>
        <ModernTable
            class="modern-table"
            :loading="loading"
            :per-page="filters.limit"
            :total="total"
            :current-page.sync="filters.page"
            :data="data"
            paginated
            hoverable
            pagination-size="is-medium"
            backend-pagination
        >
          <template #header="{ column }">
            <div :class="[{'top-sticked': isTopSticky}, column['thclass'],
         'modern-table-header-label']"
                 :ref="column['field']">
              {{ column['label'] }}
            </div>
          </template>
          <template #default="{ row }">
            <b-table-column
                label="Id"
                header-class="events-table__header-column-id"
                field="id"
                width="62px"
                cell-class="events-table__id-column">
              {{ row['id'] }}
            </b-table-column>
            <b-table-column
                label="Event"
                field="event"
                cell-class="events-table__id-column">
              {{ row['type'] }}
            </b-table-column>
            <b-table-column
                width="110px"
                centered>
              <div class="controls">
              <span v-if="!isBuiltIn(row)
                && authAuthenticator(permissions.manage.emails.template.modify)"
                    class="icon is-success action-button" @click="onEditHandler(row)">
                <i class="fas fa-pencil-alt fa-lg"></i>
              </span>
                <span v-if="!isBuiltIn(row)
                && authAuthenticator(permissions.manage.emails.template.modify)"
                      class="icon is-success action-button"
                      @click="onRemoveHandler(row)">
                <i class="fas fa-trash-alt fa-lg"></i>
              </span>
              </div>
            </b-table-column>
          </template>
          <template #bottom-left>
            <div class="modern-input-labeled">
              <label class="per-page"
                     style="font-weight: 700; white-space: nowrap">
                Per page
              </label>
              <ModernSelect
                  style="margin-right: 1em"
                  v-model="filters.limit"
                  :options="perPageOptions"
              />
            </div>
          </template>
          <template #empty v-if="!loading">
            <div style="padding-top: 1em; overflow: hidden; text-align: center">
          <span class="icon">
            <i class="fal fa-frown fa-3x"></i>
          </span>
              <p>Nothing here</p>
            </div>
          </template>
        </ModernTable>
      </template>
    </section>
  </div>
</template>


<script>
import { getEmailTemplateTypes, removeEmailTemplateType } from '@/api/emails';
import permissions from '@/assets/Permissions';
import Vue from 'vue';
import { mapGetters } from 'vuex';
import ModernInput from '@/components/ModernInput.vue';
import ModernTable from '@/components/ModernTable.vue';
import ModernSelect from '@/components/ModernSelect.vue';
import { pick } from 'lodash';
import EmailTemplateType from './Modal/EmailTemplateType.vue';

export default {
  name: 'EmailTemplates',
  components: {
    ModernInput,
    ModernTable,
    ModernSelect,
  },

  data() {
    return {
      isTopSticky: false,
      filters: {
        searchText: '',
        page: 1,
        limit: 25,
      },
      permissions,
      columns: [
        {
          field: 'id',
          label: 'Id',
          width: 60,
        },
        {
          field: 'event',
          label: 'Event',
          width: 850,
        },
      ],
      data: [],
      builtin: [],
      loading: false,
      total: 0,
      perPageOptions: [{ value: 10 }, { value: 25 }, { value: 50 }, { value: 75 }],
    };
  },

  mounted() {
    this.loadAsyncData();
  },

  computed: {
    ...mapGetters(['authAuthenticator']),
    pageOrderParams() {
      return JSON.stringify(pick(this.filters, ['page']));
    },
  },
  watch: {
    pageOrderParams() {
      this.loadAsyncData();
    },
    'filters.limit': 'doSearch',
  },

  methods: {
    doSearch() {
      this.filters.page = 1;
      this.loadAsyncData();
    },

    loadAsyncData() {
      this.loading = true;
      const queryParams = {
        page: this.filters.page,
        limit: this.filters.limit,
        filters: {
          searchText: this.filters.searchText.trim(),
        },
      };
      getEmailTemplateTypes(queryParams).then((response) => {
        this.data = response.data.data;
        this.builtin = response.data.builtin || [];
        this.total = response.data.total;
      }).finally(() => { this.loading = false; });
    },

    isBuiltIn(row) {
      return this.builtin.indexOf(row.type) !== -1;
    },

    changeModalPosition() {
      const grid = document.querySelector('.grid-module');
      const modal = document.querySelector('.modal');
      if (grid && modal) {
        grid.insertAdjacentElement('beforeend', modal);
      }
    },

    async onAddHandler() {
      const Component = Vue.extend(EmailTemplateType);
      const update = () => this.loadAsyncData();
      await this.$buefy.modal.open({
        parent: this,
        component: Component,
        hasModalCard: true,
        trapFocus: true,
        scroll: 'keep',
        ariaModal: true,
        customClass: 'card-form-modal',
        canCancel: ['outside'],
        events: {
          submit(response) {
            const { context } = response;
            const vue = context.$parent.$parent;
            const arr = vue.$data.data;
            arr.push(response.template);
            update();
          },
        },
      });
      this.changeModalPosition();
    },

    async onEditHandler(row) {
      const Component = Vue.extend(EmailTemplateType);
      const update = () => this.loadAsyncData();
      await this.$buefy.modal.open({
        parent: this,
        component: Component,
        hasModalCard: true,
        trapFocus: true,
        scroll: 'keep',
        ariaModal: true,
        customClass: 'card-form-modal',
        canCancel: ['outside'],
        props: {
          typeID: row.id,
          typeName: row.type,
        },
        events: {
          submit() {
            update();
          },
        },
      });
      this.changeModalPosition();
    },

    async onRemoveHandler(row) {
      this.$buefy.dialog.confirm({
        title: 'Deleting template event',
        message: 'Are you sure you want to <b>delete</b> this template event? This action cannot be undone.',
        confirmText: 'Delete',
        type: 'is-danger',
        hasIcon: true,
        ariaModal: true,
        onConfirm: async () => {
          this.loading = true;
          try {
            await removeEmailTemplateType({ id: row.id });
            this.loadAsyncData();
          } catch (error) {
            this.$buefy.toast.open({
              message: this.lodash.get(error, 'response.data.message', 'An unexpected error occurred, please contact support@enhancify.com'),
              type: 'is-danger',
              duration: 3000,
            });
          } finally {
            this.loading = false;
          }
        },
      });
    },
  },
};
</script>

<style lang="stylus" scoped>
@require "../assets/style/modern/variables.styl";

.sm-show
  display none
.sm-flex-column
  min-width 100%
.grid-module__filter
  padding 10px 0 20px

.icon:not([class="icon is-large"])
  color #23d160
  display inline-block
  margin 5px
.fa-eye, .fa-pencil-alt, .fa-trash-alt
  cursor pointer
.table.modern-table-table tr td,.b-table.modern-table >>> tr th
  vertical-align middle
  @media (min-width: $breakpoints.md) and (max-width: $breakpoints.lg)
    font-size 16px!important
  @media (max-width: $breakpoints.md)
    font-size 14px!important

.floating-button
  margin-left auto
  margin-right 0
@media (max-width: $breakpoints.md)
  .modern-form-group > *
    margin-right 0
  .floating-button
    margin-left 0
    width 100%
    flex-wrap wrap
    button
      width 100%
  .modern-input-controlled
    width 100%

.controls
  text-align center
.templates-table
  &__to-address-column,
  &__body-column::before,
  &__subject-column::before
    min-width 55px
</style>
